/**
 * Payment/recovery service for the Vue compatibility release.
 *
 * The Vue views read state from the server; nothing here may decide that a payment
 * succeeded. The rules match the React client:
 * - only the server's state is shown, never a locally inferred one
 * - a manual check goes through the rate-limited refresh endpoint
 * - an unknown outcome is reported as unknown, not as success
 * - recovery links are a request for proof, never a credential by themselves
 */

export const PAYMENT_STATES = ['awaiting_payment', 'paid', 'expired', 'cancelled', 'needs_review']

const LABELS = {
  awaiting_payment: 'Đang chờ chuyển khoản',
  paid: 'Đã nhận thanh toán',
  expired: 'Đã hết thời gian giữ chỗ',
  cancelled: 'Đơn đã được hủy',
  needs_review: 'Cần nhân viên kiểm tra',
}

export class PaymentError extends Error {
  constructor(code, message, { uncertain = false, retryAfterMs = null, view = null } = {}) {
    super(message)
    this.name = 'PaymentError'
    this.code = code
    this.uncertain = uncertain
    this.retryAfterMs = retryAfterMs
    this.view = view
  }
}

export function describeState(state) {
  if (!PAYMENT_STATES.includes(state)) throw new PaymentError('invalid_response', 'Trạng thái đơn không hợp lệ.')
  return {
    state,
    label: LABELS[state],
    paid: state === 'paid',
    /** Only the server may say this; the caller renders it, nothing else may set it. */
    terminal: ['paid', 'expired', 'cancelled'].includes(state),
    needsStaff: state === 'needs_review',
  }
}

export function transferDetails(view) {
  const payment = view && view.payment
  if (!payment) return null
  return {
    amount: payment.amount,
    currency: payment.currency,
    reference: payment.reference,
    expiresAt: payment.expires_at,
    bank: {
      code: payment.bank_display.bank_code,
      name: payment.bank_display.bank_name,
      accountNumber: payment.bank_display.account_number,
      accountHolder: payment.bank_display.account_holder,
    },
  }
}

export function createPaymentFlow(api) {
  return {
    /** Reads the owned checkout; the server decides what the customer sees. */
    async load(checkoutId) {
      const view = await api.readCheckout(checkoutId)
      if (!view || view.state === undefined) throw new PaymentError('invalid_response', 'Phản hồi không hợp lệ.')
      return view
    },

    /**
     * Asks the server to reconcile. `key` must stay the same while an outcome is unknown so
     * a retry cannot double-count; the reset path lets the caller rotate it after a definitive
     * answer.
     */
    async check(checkoutId, key) {
      try {
        const view = await api.refreshPayment(checkoutId, key)
        return { view, state: describeState(view.state), uncertain: false }
      } catch (error) {
        if (error && error.code === 'rate_limited') {
          throw new PaymentError('rate_limited', 'Vui lòng chờ trước khi kiểm tra lại.', {
            retryAfterMs: error.retryAfterMs ?? null,
          })
        }
        if (error && (error.uncertain || error.code === 'timeout' || error.code === 'network_error')) {
          throw new PaymentError('uncertain', 'Chưa rõ kết quả kiểm tra. Hãy thử lại bằng cùng yêu cầu.', { uncertain: true })
        }
        if (error && error.code === 'unauthenticated') {
          throw new PaymentError('unauthenticated', 'Phiên truy cập đã hết hạn. Vui lòng mở lại liên kết đơn.')
        }
        throw new PaymentError(error?.code || 'internal_error', 'Không kiểm tra được thanh toán.')
      }
    },

    /** Legacy links need proof sent to the contact on file; they never grant access by themselves. */
    async startRecovery(legacyToken, key) {
      if (typeof legacyToken !== 'string' || !legacyToken) throw new PaymentError('invalid_request', 'Liên kết không hợp lệ.')
      try {
        return await api.startRecovery(legacyToken, key)
      } catch (error) {
        if (error && error.code === 'rate_limited') {
          throw new PaymentError('rate_limited', 'Vui lòng chờ trước khi yêu cầu lại.', { retryAfterMs: error.retryAfterMs ?? null })
        }
        throw new PaymentError('invalid_request', 'Không gửi được yêu cầu xác minh cho liên kết này.')
      }
    },

    /** One-use proof: a valid proof returns the owned checkout, an invalid one says nothing else. */
    async redeemRecovery(token, key) {
      if (typeof token !== 'string' || !token) throw new PaymentError('invalid_request', 'Mã xác minh không hợp lệ.')
      try {
        const view = await api.redeemRecovery(token, key)
        if (!view || view.state === undefined) throw new PaymentError('invalid_response', 'Phản hồi không hợp lệ.')
        return view
      } catch (error) {
        if (error instanceof PaymentError) throw error
        throw new PaymentError('not_found', 'Mã xác minh không đúng hoặc đã được dùng.')
      }
    },
  }
}
